import { AxiosError } from 'axios';
import { TFunction } from 'i18next';
import { IAPIError, MatterRequestException } from '../interface/common';
import { STATUS_CODE } from '../constants/constants';

const asApiError = (error: unknown) => error as AxiosError<IAPIError> | undefined;

export const isForbiddenError = (error: unknown): boolean =>
  asApiError(error)?.response?.status === STATUS_CODE.FORBIDDEN_EXCEPTION;

/**
 * The client applies the same permission rules before offering the actions, so these only surface when
 * the rights or the user's roles changed after the form was opened. A restricted user who lost access to
 * the request itself gets a 404, because the API reads it back through their own visibility scope.
 */
export const getMatterRequestUpdateErrorMessage = (error: unknown, t: TFunction): string => {
  if (isForbiddenError(error)) return t('matterRequest:updateError.editingNotAllowed');

  if (asApiError(error)?.response?.status === STATUS_CODE.ENTITY_NOT_FOUND) {
    return t('matterRequest:updateError.noLongerAvailable');
  }

  switch (asApiError(error)?.response?.data?.type) {
    case MatterRequestException.InsiderMatterRequestPermissionDeniedException:
      return t('matterRequest:updateError.insiderPermissionDenied');
    case MatterRequestException.MatterRequestProtectedFieldsPermissionDeniedException:
      return t('matterRequest:updateError.peopleFieldsPermissionDenied');
  }

  return t('common:somethingWentWrongTryAgain');
};

export const isPrincipalCustomerInactiveError = (error: unknown): boolean =>
  !isForbiddenError(error) &&
  asApiError(error)?.response?.data?.type ===
    MatterRequestException.ValidatePrincipalCustomerIsActiveException;

export const getMatterRequestCreateErrorMessage = (error: unknown, t: TFunction): string => {
  if (isForbiddenError(error)) return t('matterRequest:createError.creatingNotAllowed');

  if (isPrincipalCustomerInactiveError(error)) {
    return t('matterRequest:createError.principalCustomerInactive');
  }

  return (error as Error | undefined)?.message || t('common:somethingWentWrongTryAgain');
};
